import { CSSProperties, ReactElement, useEffect, useMemo, useRef } from "react";
import {
  DynamicRowHeight,
  List,
  RowComponentProps,
  useDynamicRowHeight,
} from "react-window";

import { useWorkspaceConnections } from "@web-app/api";
import { OrderCard, RouteCard, Skeleton } from "@web-app/components";
import { focusOnOrder } from "@web-app/pages/common/DispatchMap/mapFocusUtils";
import useDispatchStore from "@web-app/store/dispatchStore";
import {
  BaseApp,
  Order,
  Route,
  User,
  WorkspaceConnection,
} from "@web-app/types";

type ItemData = {
  data: Order | Route;
  type: "order" | "route";
};

type ItemsType = Map<number, ItemData>;

type RowData = {
  appsMap: Map<string, BaseApp>;
  assigneesMap: Map<string, User>;
  autoAssignSearchingIds: Set<string>;
  currentUser: Omit<User, "createdAt"> | null;
  dynamicRowHeight: DynamicRowHeight;
  isAppsLoading?: boolean;
  isAssigneesLoading?: boolean;
  items: ItemsType;
  onSelectExternalProvider: (
    entity: Order | Route | null,
    provider: string,
    type: string,
  ) => void;
  onShare?: (orderId: string, connectedWorkspaceId: string) => void;
  routedOrdersMap: Map<string, Order>;
  searchTerm: string;
  workspaceConnections: WorkspaceConnection[];
};

export default function ListView({
  appsMap,
  assigneesMap,
  autoAssignSearchingIds,
  isAppsLoading,
  isAssigneesLoading,
  onSelectExternalProvider,
  onShare,
  unroutedOrders,
  routedOrdersMap,
  routes,
  searchTerm,
  isLoadingOrders,
}: {
  appsMap: Map<string, BaseApp>;
  assigneesMap: Map<string, User>;
  autoAssignSearchingIds: Set<string>;
  isAppsLoading?: boolean;
  isAssigneesLoading?: boolean;
  isLoadingOrders: boolean;
  onSelectExternalProvider: (
    entity: Order | Route | null,
    provider: string,
    type: string,
  ) => void;
  onShare?: (orderId: string, connectedWorkspaceId: string) => void;
  routedOrdersMap: Map<string, Order>;
  routes: Route[];
  searchTerm: string;
  unroutedOrders: Order[];
}) {
  const { data: workspaceConnections } = useWorkspaceConnections();
  const currentUser = useDispatchStore((state) => state.user);

  const items: ItemsType = useMemo(() => {
    return new Map(
      [...routes, ...unroutedOrders].map((item, index) => [
        index,
        {
          data: item,
          type: routes.length > index ? "route" : "order",
        },
      ]),
    );
  }, [routes, unroutedOrders]);

  const dataVersion = useMemo(() => {
    const routeKeys = routes.map(
      (r) => `r-${r.id}-${r.updatedAt?.getTime?.() || 0}`,
    );
    const orderKeys = unroutedOrders.map(
      (o) => `o-${o.id}-${o.updatedAt?.getTime?.() || 0}`,
    );
    return [...routeKeys, ...orderKeys].join("|");
  }, [routes, unroutedOrders]);

  const dynamicRowHeight = useDynamicRowHeight({
    defaultRowHeight: 80,
  });

  if (isLoadingOrders) {
    return (
      <div className="h-full flex flex-col items-center gap-3 w-full px-4">
        <Skeleton
          height="h-[80px] lg:h-[120px]"
          width="w-full"
          className="rounded-xl"
        />
        <Skeleton
          height="h-[80px] lg:h-[120px]"
          width="w-full"
          className="rounded-xl"
        />
        <Skeleton
          height="h-[80px] lg:h-[120px]"
          width="w-full"
          className="rounded-xl"
        />
        <Skeleton
          height="h-[80px] lg:h-[120px]"
          width="w-full"
          className=" bg-gradient-to-b from-gray-200 !bg-white to-gray-50 rounded-xl"
        />
      </div>
    );
  }

  // Virtual scrolling
  if (items.size > 0) {
    const rowProps: RowData = {
      appsMap,
      assigneesMap,
      autoAssignSearchingIds,
      currentUser,
      dynamicRowHeight,
      isAppsLoading,
      isAssigneesLoading,
      items,
      onSelectExternalProvider,
      onShare,
      routedOrdersMap,
      searchTerm,
      workspaceConnections: workspaceConnections || [],
    };

    return (
      <div className="h-full w-full overflow-hidden">
        <List
          key={dataVersion}
          className="pb-20"
          rowComponent={RowComponent}
          rowCount={items.size}
          rowHeight={dynamicRowHeight}
          rowProps={rowProps}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full text-gray-500">
      <p>No orders or routes to display</p>
    </div>
  );
}

function RowComponent({
  index,
  style,
  appsMap,
  assigneesMap,
  autoAssignSearchingIds,
  currentUser,
  dynamicRowHeight,
  isAppsLoading,
  isAssigneesLoading,
  items,
  onSelectExternalProvider,
  onShare,
  routedOrdersMap,
  searchTerm,
  workspaceConnections,
}: RowComponentProps<RowData>): ReactElement | null {
  const rowRef = useRef<HTMLDivElement>(null);

  // Observe the row element for height changes
  useEffect(() => {
    if (rowRef.current) {
      const cleanup = dynamicRowHeight.observeRowElements([rowRef.current]);
      return cleanup;
    }
  }, [dynamicRowHeight]);

  const item = items.get(index);

  if (!item) return null;

  const isRoute = item.type === "route";

  const rowStyle: CSSProperties = {
    ...style,
    height: "auto",
    minHeight: style.height,
  };

  if (isRoute) {
    const route = item.data as Route;
    return (
      <div ref={rowRef} style={rowStyle} className="pb-2 px-4">
        <RouteCard
          route={route}
          routedOrdersMap={routedOrdersMap}
          searchTerm={searchTerm}
          onSelectExternalProvider={onSelectExternalProvider}
          assigneesMap={assigneesMap}
          isAssigneesLoading={isAssigneesLoading}
          isAutoAssignSearching={autoAssignSearchingIds.has(route.id)}
          appsMap={appsMap}
          isAppsLoading={isAppsLoading}
        />
      </div>
    );
  }

  const order = item.data as Order;
  return (
    <div ref={rowRef} style={rowStyle} className="pb-2 px-4">
      <OrderCard
        order={order}
        app={order.appId ? appsMap.get(order.appId) : null}
        isAppsLoading={isAppsLoading}
        assignee={order.assigneeId ? assigneesMap.get(order.assigneeId) : null}
        isAssigneesLoading={isAssigneesLoading}
        isAutoAssignSearching={autoAssignSearchingIds.has(order.id)}
        searchTerm={searchTerm}
        workspaceConnections={workspaceConnections}
        onViewDetails={() => {
          useDispatchStore.setState({
            focusedOrderId: order.id,
            mapFocusedOrderId: order.id,
          });

          setTimeout(() => {
            const { dispatchMapRef } = useDispatchStore.getState();
            if (dispatchMapRef?.current) {
              focusOnOrder({
                mapRef: dispatchMapRef.current,
                order,
              });
            }
          }, 0);
        }}
        onShare={
          currentUser?.currentWorkspaceId === order?.workspaceId && onShare
            ? (connectedWorkspaceId) => onShare(order.id, connectedWorkspaceId)
            : undefined
        }
        onSelectExternalProvider={onSelectExternalProvider}
      />
    </div>
  );
}
